import { Col, Container, Row, View } from "@amzn/stencil-react-components/layout";
import { useBreakpoints } from "@amzn/stencil-react-components/responsive";
import React, { FC, ReactNode } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { OrderTrackingHeader } from "@/pages/OrderTracking/components/OrderTrackingHeader/order-tracking-header";
import { OrderProgressSteps } from "@/pages/OrderTracking/components/OrderProgressSteps/order-progress-steps";
import { OrderedItems } from "@/pages/OrderTracking/components/OrderedItems/ordered-items";
import { OrderSummaryPanel } from "@/pages/OrderTracking/components/OrderSummaryPanel/order-summary-panel";
import { NotFoundLink, OrderLoadError, OrderLoading, OrderNotFound } from "./order-tracking-states";
import { NotificationBanners } from "../../../../common/components/NotificationBanners/notification-banners";
import { useUnknownStatusBanner } from "../../hooks/use-unknown-status-banner.hook";
import type { OrderTrackingView } from "@/pages/OrderTracking/order-tracking.types";

export type { NotFoundLink };

const COLUMNS_DIRECTION_BY_BREAKPOINT = {
  s: { flexDirection: "column" },
  m: { flexDirection: "column" },
  default: { flexDirection: "row" },
};

export interface OrderTrackingLayoutProps {
  /** Absent once loading has finished means the order was not found. */
  view?: OrderTrackingView;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  notFoundLink: NotFoundLink;
  /** Rendered below the summary panel, in the summary column. */
  summaryAction?: ReactNode;
  /** Replaces the header's default support link. */
  headerAction?: ReactNode;
}

interface FoundOrderProps {
  view: OrderTrackingView;
  summaryAction?: ReactNode;
  headerAction?: ReactNode;
}

const FoundOrder: FC<FoundOrderProps> = ({ view, summaryAction, headerAction }) => {
  const { props: columnsDirection } = useBreakpoints({ propsMap: COLUMNS_DIRECTION_BY_BREAKPOINT });
  useUnknownStatusBanner(!view.steps);

  return (
    <Col gridGap="S400" width="100%">
      <OrderTrackingHeader orderNumber={view.orderNumber} supportLink={headerAction} />
      <NotificationBanners />
      <Row gridGap="S400" width="100%" alignItems="flex-start" {...columnsDirection}>
        <Col gridGap="S300" flex={2} width="100%">
          {view.steps && <OrderProgressSteps steps={view.steps} />}
          <OrderedItems item={view.item} />
        </Col>
        <Col gridGap="S300" flex={1} width="100%">
          <aside aria-label={getString("orderTracking.summaryLabel")}>
            <OrderSummaryPanel orderNumber={view.orderNumber} rows={view.summaryRows} />
          </aside>
          {summaryAction && <View width="100%">{summaryAction}</View>}
        </Col>
      </Row>
    </Col>
  );
};

const OrderTrackingContent: FC<OrderTrackingLayoutProps> = ({
  view,
  isLoading,
  isError,
  onRetry,
  notFoundLink,
  summaryAction,
  headerAction,
}) => {
  if (isLoading) {
    return <OrderLoading />;
  }
  if (isError) {
    return <OrderLoadError onRetry={onRetry} />;
  }
  if (!view) {
    return <OrderNotFound link={notFoundLink} />;
  }
  return <FoundOrder view={view} summaryAction={summaryAction} headerAction={headerAction} />;
};

export const OrderTrackingLayout: FC<OrderTrackingLayoutProps> = (props) => (
  <Container maxWidth="100%" paddingHorizontal="S700" paddingTop="S300" paddingBottom="S500">
    <OrderTrackingContent {...props} />
  </Container>
);
